import React from 'react';
import { Shield, Lock, FileText, Mail } from 'lucide-react';
import { SEO } from '../lib/seo';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <SEO 
        title="Política de Privacidad | Electricista Barcelona"
        description="Política de privacidad y protección de datos. Información sobre el tratamiento de datos personales y derechos RGPD."
        canonical="/privacy-policy"
        type="website"
      />
      
      <div className="py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-8">Política de Privacidad</h1>

          <div className="prose max-w-none">
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <h2 className="text-2xl font-bold">Protección de Datos Personales</h2>
              </div>
              
              <p className="mb-4">
                De acuerdo con el Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica de Protección de Datos Personales y garantía de los derechos digitales (LOPD-GDD), le informamos que sus datos serán tratados con la finalidad de gestionar su solicitud y prestarle nuestros servicios.
              </p>

              <h3 className="text-xl font-bold mt-8 mb-4">Responsable del Tratamiento</h3>
              <ul className="list-none space-y-2">
                <li><strong>Nombre:</strong> Manel Belmonte Montesinos</li>
                <li><strong>Dirección:</strong> Carrer de Trinxant, 128, local derecho, San Andrés de Palomar, 08041 Barcelona</li>
                <li><strong>Email:</strong> info@electricista-urgencias-24-horas-barcelona.com</li>
                <li><strong>Teléfono:</strong> +34 600 23 82 83</li>
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <h2 className="text-2xl font-bold">Datos que Recopilamos</h2>
              </div>

              <p className="mb-4">Los datos personales que tratamos son:</p>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li>Nombre y apellidos</li>
                <li>Dirección de la instalación</li>
                <li>Número de teléfono</li>
                <li>Dirección de correo electrónico</li>
                <li>Imágenes proporcionadas por el usuario (facturas, instalaciones)</li>
              </ul>

              <h3 className="text-xl font-bold mt-8 mb-4">Finalidad del Tratamiento</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Gestión y prestación de servicios solicitados</li>
                <li>Emisión de presupuestos</li>
                <li>Facturación y cobro</li>
                <li>Comunicaciones relacionadas con el servicio</li>
                <li>Cumplimiento de obligaciones legales</li>
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <h2 className="text-2xl font-bold">Base Legal</h2>
              </div>

              <p className="mb-4">El tratamiento de sus datos está basado en:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>El consentimiento del interesado</li>
                <li>La ejecución de un contrato o prestación de servicios</li>
                <li>El cumplimiento de obligaciones legales</li>
                <li>El interés legítimo del responsable</li>
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <h2 className="text-2xl font-bold">Sus Derechos</h2>
              </div>

              <p className="mb-4">Usted tiene derecho a:</p>
              <ul className="list-disc list-inside space-y-2 mb-6">
                <li>Acceder a sus datos personales</li>
                <li>Rectificar los datos inexactos</li>
                <li>Solicitar su supresión</li>
                <li>Oponerse al tratamiento</li>
                <li>Limitar su tratamiento</li>
                <li>Portabilidad de los datos</li>
              </ul>

              <div className="bg-yellow-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Ejercicio de Derechos</h3>
                <p className="mb-4">
                  Para ejercer sus derechos, puede contactarnos a través de:
                </p>
                <ul className="list-none space-y-2">
                  <li>
                    <strong>Email:</strong>{' '}
                    <a 
                      href="mailto:info@electricista-urgencias-24-horas-barcelona.com"
                      className="text-yellow-600 hover:text-yellow-700"
                    >
                      info@electricista-urgencias-24-horas-barcelona.com
                    </a>
                  </li>
                  <li>
                    <strong>Teléfono:</strong>{' '}
                    <a 
                      href="tel:+34600238283"
                      className="text-yellow-600 hover:text-yellow-700"
                    >
                      +34 600 23 82 83
                    </a>
                  </li>
                  <li>
                    <strong>Dirección Postal:</strong>{' '}
                    <span>Carrer de Trinxant, 128, local derecho, San Andrés de Palomar, 08041 Barcelona</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">Conservación de los Datos</h2>
              <p>
                Sus datos serán conservados mientras se mantenga la relación comercial o durante los años necesarios para cumplir con las obligaciones legales. En el caso de las imágenes proporcionadas, estas se eliminarán una vez finalizado el servicio y cumplidas las obligaciones legales correspondientes.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">Medidas de Seguridad</h2>
              <p className="mb-4">
                Hemos adoptado todas las medidas técnicas y organizativas necesarias para garantizar la seguridad de los datos personales tratados y evitar su alteración, pérdida, tratamiento o acceso no autorizado.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Modificaciones de la Política de Privacidad</h2>
              <p>
                Nos reservamos el derecho a modificar la presente política de privacidad para adaptarla a novedades legislativas o jurisprudenciales, así como a prácticas de la industria. En dichos supuestos, anunciaremos en esta página los cambios introducidos con razonable antelación a su puesta en práctica.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;